import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Switch,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
  Tooltip
} from '@chakra-ui/react';
import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import FilterTextBox from '../../components/FilterTextBox';
import { WebAPIContext } from '../../contexts/WebAPIContext';
import { JIS_DEPARTMENT_CLASSES, Messages, InputLimits } from '../../contexts/constants';
import { formatLocalDateTime } from '../../utils/utils';
import { supportedLngs } from '../../utils/i18n';
import {
  BilingualName,
  EquipmentCategoryType,
  Errors,
  LanguageCode
} from '../../contexts/types';

/** ステータスフィルター値 */
type StatusFilter = 'all' | 'official' | 'candidate';

/**
 * マスタメンテナンス - 設備分類 画面
 */
const EquipmentCategory: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const WebAPI = useContext(WebAPIContext);

  /** タイトル */
  const TITLE = t('features.MasterMaintenance.EquipmentCategory.title');

  /** ヘッダー部セルスタイル */
  const headerCellStyle = {
    fontWeight: "bold",
    fontSize: "14px",
    whiteSpace: "normal",
    wordBreak: "break-word" as const,
    backgroundColor: "gray.100",
  };

  /** ボディ部セルスタイル */
  const bodyCellStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    bgColor: "white",
  }

  /** 列固定スタイル */
  const fixedColCellStyle = {
    position: "sticky" as const,
    left: "0",
    zIndex: "1",
    boxShadow: "1px 0 0 #ccc",
  }

  /** ホバースタイル */
  const hoverStyle = {
    bg: "gray.200"
  }

  /** 項目（ラベル）スタイル */
  const labelStyle = {
    fontWeight: "bold",
    whiteSpace: "normal" as const,
    wordBreak: "break-word" as const,
    marginTop: "8px",
    width: 170,
  }

  /** 項目（値）幅 */
  const valueWidth = 475;

  /** SUPPORTED_LANGUAGES/supportedLngsに追従する動的言語リスト */
  const langs = Object.keys(supportedLngs) as LanguageCode[];
  /** GraphQL selectionSet用: "ja en" 等 */
  const langFields = langs.join(' ');

  /** 設備分類マスタデータ */
  const [masterData, setMasterData] = useState<EquipmentCategoryType[]>([]);

  /** 編集対象 */
  const [editTarget, setEditTarget] = useState<EquipmentCategoryType>({});

  /** エラー情報 */
  const [errors, setErrors] = useState<Errors>({});

  /** モーダル表示制御用 */
  const { isOpen: isOpenEditModal, onOpen: onOpenEditModal, onClose: onCloseEditModal } = useDisclosure();

  // （フィルター）設備分類名を管理
  const [filterCategoryName, setFilterCategoryName] = useState<string>("");

  // ステータスフィルター
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // 編集モーダル保存ボタンの活性制御
  const [isSaving, setIsSaving] = useState(false);

  // コードプレフィックス: JIS部門分類コード
  const [jisClassCode, setJisClassCode] = useState<string>("B");

  // コードプレフィックス: 数字部分
  const [detailCode, setDetailCode] = useState<string>("");

  /** 仮登録件数 */
  const candidateCount = useMemo(() => {
    return (masterData ?? []).filter(m => m.isCandidate && !m.deleted).length;
  }, [masterData]);

  /** フィルター済みマスタデータ */
  const filteredMasterData = (masterData ?? []).filter(m => {
    if (!m) return false;
    const hasFilterString = (propertyValue: string | null | undefined, filterString: string): boolean => {
      return (propertyValue ?? '').toLowerCase().includes(filterString.toLowerCase());
    };
    // ステータスフィルター
    if (statusFilter === 'official' && m.isCandidate) return false;
    if (statusFilter === 'candidate' && !m.isCandidate) return false;
    return langs.some((lang) => hasFilterString(m.equipmentCategoryName?.[lang], filterCategoryName));
  });

  /**
   * 画面初期表示
   */
  useEffect(() => {
    const fetch = async () => {
      await fetchMasterData();
    }
    fetch();
  }, []);

  /**
   * 「新規登録」ボタン押下
   */
  const handleOpenEditModal = async () => {
    onOpenEditModal();
  }

  /**
   * 指定したプロパティ名の値を変更
   */
  const changeEditTarget = (propertyName: string, value: any) => {
    setEditTarget((prevState) => ({
      ...prevState,
      [propertyName]: value,
    }));
  }

  /**
   * 対象のマスタデータ取得
   */
  const fetchMasterData = async () => {
    try {
      const payload = {
        query: `
          query {
            getEquipmentCategory(hasDeleted: true) {
              equipmentCategoryId
              equipmentCategoryName {${langFields}}
              description
              codePrefix
              sortOrder
              isCandidate
              registeredBy
              deleted
              createDate
              updateDate
            }
          }`
      };

      const response = await WebAPI.request(payload);

      if (response?.data?.getEquipmentCategory) {
        setMasterData(response?.data?.getEquipmentCategory);
      } else {
        console.error(`Failed to call API getEquipmentCategory. errors = `, response?.errors);
        toast({
          title: TITLE,
          description: t('common.message.fetchListFailed', { name: TITLE }),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }

    } catch (error) {
      console.error("Failed. fetchData", error);
      toast({
        title: TITLE,
        description: t('common.message.fetchListFailed', { name: TITLE }),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }

  /**
   * 「設備分類名」リンク押下
   */
  const handleEdit = async (record: EquipmentCategoryType) => {
    setEditTarget({ ...record });
    // 既存のcodePrefixをJIS分類コードと数字に分解
    const match = (record.codePrefix || "").match(/^([A-Za-z])(\d*)$/);
    if (match) {
      setJisClassCode(match[1].toUpperCase());
      setDetailCode(match[2]);
    } else {
      setJisClassCode("B");
      setDetailCode("");
    }
    onOpenEditModal();
  }

  /**
   * 「保存」ボタン押下
   */
  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);

    if (!validate()) {
      toast({
        title: t('common.label.inputError'),
        description: t('common.message.inputErrorContent'),
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      setIsSaving(false);
      return;
    }

    if (!await putEditTargetWith(editTarget, true)) {
      setIsSaving(false);
      return;
    }

    await fetchMasterData();

    setIsSaving(false);
    onCloseEditModal();
  }

  /**
   * 「確定する」ボタン押下（仮登録→正式採用）
   */
  const handlePromote = async () => {
    if (isSaving) return;
    setIsSaving(true);

    if (!validate()) {
      toast({
        title: t('common.label.inputError'),
        description: t('common.message.inputErrorContent'),
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      setIsSaving(false);
      return;
    }

    const promoted = { ...editTarget, isCandidate: false };

    if (!await putEditTargetWith(promoted, false)) {
      setIsSaving(false);
      return;
    }

    setEditTarget(promoted);

    toast({
      title: TITLE,
      description: t('common.message.promoteToOfficial'),
      status: 'success',
      duration: 5000,
      isClosable: true,
    });

    await fetchMasterData();

    setIsSaving(false);
    onCloseEditModal();
  }

  /**
   * バリデーションチェック
   */
  const validate = () => {
    setErrors({});
    const localErrors: Errors = {};
    const required = (value?: string | null) => Boolean(value || null);
    const requiredAndSetError = (target: any, propertyName: string, propertyNameJp: string, errorKey: string = propertyName) => {
      const requiredResult = required(target?.[propertyName]);
      if (!requiredResult) {
        localErrors[errorKey] = {
          isError: true,
          errorMessage: t('common.message.requiredField', { fieldName: propertyNameJp }),
        };
      }
      return requiredResult;
    }

    let result = true;

    // 設備分類名の動的バリデーション (ja, en等)
    langs.forEach((lang) => {
      // 最初の文字を大文字にする (例: 'ja' -> 'Ja', 'en' -> 'En')
      const CapitalizedLang = lang.charAt(0).toUpperCase() + lang.slice(1);

      // エラーキー: 既存仕様通り "equipmentCategoryNameJa", "equipmentCategoryNameEn" になる
      const errorKey = `equipmentCategoryName${CapitalizedLang}`;

      // 翻訳キー: 既存仕様通り 'ja' の時は "field.name"、それ以外は "field.nameEn" などを参照
      const translationKey = lang === 'ja'
        ? 'features.MasterMaintenance.EquipmentCategory.field.name'
        : `features.MasterMaintenance.EquipmentCategory.field.name${CapitalizedLang}`;

      if (!requiredAndSetError(editTarget.equipmentCategoryName, lang, t(translationKey), errorKey)) {
        result = false;
      } else if (lang !== 'ja' && editTarget.equipmentCategoryName?.[lang] && editTarget.equipmentCategoryName[lang]!.length > 100) {
        const tooLongTranslationKey = `features.MasterMaintenance.EquipmentCategory.error.tooLongName${CapitalizedLang}`;
        localErrors[errorKey] = {
          isError: true,
          errorMessage: t(tooLongTranslationKey),
        };
        result = false;
      }
    });
    if (!requiredAndSetError(editTarget, "codePrefix", t('features.MasterMaintenance.EquipmentCategory.field.codePrefix'))) {
      result = false;
    } else if (editTarget.codePrefix && !/^[A-Z]\d{1,2}$/.test(editTarget.codePrefix)) {
      localErrors["codePrefix"] = {
        isError: true,
        errorMessage: t('features.MasterMaintenance.EquipmentCategory.error.invalidJisFormat'),
      };
      result = false;
    }

    setErrors(localErrors);
    return result;
  }

  /**
   * 指定したデータで登録・更新
   * @param target 保存対象データ
   * @param showSuccessToast 成功時のtoast表示（promote時はfalseにして呼び出し元で表示）
   */
  const putEditTargetWith = async (target: EquipmentCategoryType, showSuccessToast: boolean) => {
    try {
      const input = {
        equipmentCategoryId: target.equipmentCategoryId,
        // langを使用して動的に追加する
        equipmentCategoryName: langs.reduce((acc, lang) => {
          acc[lang] = target.equipmentCategoryName?.[lang] || '';
          return acc;
        }, {} as BilingualName),
        description: target.description || null,
        codePrefix: target.codePrefix || null,
        sortOrder: (target.sortOrder != null && !isNaN(target.sortOrder)) ? target.sortOrder : 0,
        isCandidate: target.isCandidate ?? false,
        deleted: target.deleted,
        updateDate: target.updateDate,
      };

      const result = await WebAPI.request({
        query: `
          mutation putEquipmentCategory($input: EquipmentCategoryInput!) {
            putEquipmentCategory(input: $input) {status, message}
          }
        `,
        variables: {
          input: input,
        }
      });

      if (result?.data?.putEquipmentCategory?.status === "success") {
        if (showSuccessToast) {
          toast({
            title: TITLE,
            description: t('common.message.inputSaved'),
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
        }
        return true;
      } else if (result?.data?.putEquipmentCategory?.status === 'expired') {
        toast({
          title: TITLE,
          description: Messages.expiredMessage,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        return false;
      } else {
        const apiMessage = result?.data?.putEquipmentCategory?.message;
        toast({
          title: TITLE,
          description: apiMessage ? t(apiMessage) : t('common.message.saveFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error(`Failed to call API putEquipmentCategory. errors = ${JSON.stringify(result?.errors)}`);
        return false;
      }
    } catch (error) {
      console.error("Exception to call API putEquipmentCategory. error = ", error);
      toast({
        title: TITLE,
        description: t('common.message.saveFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return false;
    }
  }

  /**
   * 「閉じる」ボタン押下
   */
  const handleClose = async () => {
    await fetchMasterData();
    onCloseEditModal();
  }

  /**
   * 編集モーダル閉じる時、初期化する
   */
  const handleCloseComplete = () => {
    setEditTarget({});
    setErrors({});
    setJisClassCode("B");
    setDetailCode("");
  }

  /**
   * レイアウト
   */
  return (
    <>
      <Box h="100%" mt="16px">
        <HStack spacing={4}>
          <Button colorScheme='blue' onClick={(e) => {
            e.currentTarget.blur();
            handleOpenEditModal()
          }}>{t('common.button.newRegistration')}</Button>

          {/* ステータスフィルター */}
          <HStack spacing={2}>
            <Text fontWeight="bold" whiteSpace="nowrap">{t('common.field.status')}:</Text>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              width="160px"
              size="sm"
            >
              <option value="all">{t('common.label.all')}</option>
              <option value="official">{t('common.label.official')}</option>
              <option value="candidate">{t('common.label.candidate')}</option>
            </Select>
            {candidateCount > 0 && (
              <Badge colorScheme="orange" fontSize="sm" px={2} py={1}>
                {t('common.label.candidate')}: {candidateCount}{t('common.label.count')}
              </Badge>
            )}
          </HStack>
        </HStack>
        <TableContainer overflowX="auto" overflowY="auto" w="100%" h="calc(100dvh - 150px)" mt={2}>
          <Table variant="simple" style={{ tableLayout: "fixed", borderCollapse: "separate" }} cellSpacing={0} cellPadding={0} className="mm-ceas-table">
            <colgroup>
              <col width={120} />
              {langs.map((lang) => (
                <col key={`col-equipmentCategoryName-${lang}`} width={250} />
              ))}
              <col width={350} />
              <col width={180} />
              <col width={100} />
              <col width={120} />
              <col width={120} />
              <col width={230} />
              <col width={230} />
            </colgroup>
            <Thead position="sticky" top={0} zIndex={2} boxShadow={`0 -1px 0 #EEEEEE`}>
              <Tr h={50}>
                <Th {...headerCellStyle} {...fixedColCellStyle}>{t('features.MasterMaintenance.EquipmentCategory.field.id')}</Th>
                {langs.map((lang) => {
                  // 'ja' の時は既存仕様通り "field.name"、それ以外は "field.nameEn" などを参照（動的ループ）
                  const CapitalizedLang = lang.charAt(0).toUpperCase() + lang.slice(1);
                  const translationKey = lang === 'ja'
                    ? 'features.MasterMaintenance.EquipmentCategory.field.name'
                    : `features.MasterMaintenance.EquipmentCategory.field.name${CapitalizedLang}`;
                  return (
                    <Th {...headerCellStyle} key={`th-equipmentCategoryName-${lang}`}>
                      {t(translationKey)}
                      {lang === 'ja' && (
                        <FilterTextBox placeholder={t('common.placeholder.partialMatch')} filterValue={filterCategoryName} setFilterValue={setFilterCategoryName} />
                      )}
                    </Th>
                  );
                })}
                <Th {...headerCellStyle}>{t('features.MasterMaintenance.EquipmentCategory.field.description')}</Th>
                <Th {...headerCellStyle}>{t('features.MasterMaintenance.EquipmentCategory.field.codePrefix')}</Th>
                <Th {...headerCellStyle}>{t('common.label.sortOrder')}</Th>
                <Th {...headerCellStyle}>{t('common.field.status')}</Th>
                <Th {...headerCellStyle}>{t('common.label.deleteFlag')}</Th>
                <Th {...headerCellStyle}>{t('common.label.registrationDate')}</Th>
                <Th {...headerCellStyle}>{t('common.label.updateDate')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredMasterData.map(record => {
                const bgColor = record.deleted ? "gray.100" : record.isCandidate ? "orange.50" : "white";

                return (
                  <Tr role="group" key={record.equipmentCategoryId} _hover={{ bg: "gray.200" }}>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} {...fixedColCellStyle}>
                      {record.equipmentCategoryId}
                    </Td>
                    {langs.map((lang) => (
                      <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} key={`td-equipmentCategoryName-${lang}-${record.equipmentCategoryId}`}>
                        {lang === 'ja' ? (
                          <Tooltip label={record.equipmentCategoryName?.[lang]} placement="top-start" hasArrow openDelay={500}>
                            <Button
                              variant="link"
                              textDecoration="underline"
                              color="blue.500"
                              onClick={(e) => {
                                e.currentTarget.blur();
                                handleEdit(record)
                              }}
                              sx={{
                                display: 'inline-block',
                                maxW: '100%',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {record.equipmentCategoryName?.[lang]}
                              {record.isCandidate && <Tag size="sm" colorScheme="orange" ml={1}>{t('common.label.candidate')}</Tag>}
                            </Button>
                          </Tooltip>
                        ) : (
                          <Tooltip label={record.equipmentCategoryName?.[lang]} placement="top-start" hasArrow openDelay={500}>
                            <Text isTruncated >{record.equipmentCategoryName?.[lang]}</Text>
                          </Tooltip>
                        )}
                      </Td>
                    ))}
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} whiteSpace="normal">
                      {record.description}
                    </Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>
                      {record.codePrefix}
                    </Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} textAlign="right">
                      {record.sortOrder}
                    </Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} textAlign="center">
                      {record.isCandidate ? (
                        <Tag size="sm" colorScheme="orange">{t('common.label.candidate')}</Tag>
                      ) : (
                        <Tag size="sm" colorScheme="blue">{t('common.label.official')}</Tag>
                      )}
                    </Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} textAlign="center">{record.deleted ? (
                      <Tag size="lg" colorScheme='red'>{String(record.deleted)}</Tag>
                    ) : (
                      <Tag size="lg" colorScheme='green'>{String(record.deleted)}</Tag>
                    )}</Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>{formatLocalDateTime(record.createDate)}</Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>{formatLocalDateTime(record.updateDate)}</Td>
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      {/* 編集モーダル */}
      <Modal isOpen={isOpenEditModal} onClose={onCloseEditModal} closeOnOverlayClick={false} onCloseComplete={handleCloseComplete} scrollBehavior="inside" size="3xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack>
              <Text>{t('features.MasterMaintenance.EquipmentCategory.modal.editTitle')}</Text>
              {editTarget.isCandidate && <Tag colorScheme="orange">{t('common.label.candidate')}</Tag>}
            </HStack>
          </ModalHeader>
          <ModalBody>
            <VStack>

              <FormControl h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('features.MasterMaintenance.EquipmentCategory.field.id')}
                  </FormLabel>
                  <VStack align="left">
                    <Input
                      value={editTarget.equipmentCategoryId || t('common.label.autoNumber')}
                      width={valueWidth}
                      isDisabled={true}
                      bg="gray.100"
                    />
                  </VStack>
                </Flex>
              </FormControl>

              {/* 設備分類名（多言語）入力欄: SUPPORTED_LANGUAGES/supportedLngsに追従する動的ループ */}
              {langs.map((lang) => {
                const CapitalizedLang = lang.charAt(0).toUpperCase() + lang.slice(1);
                // エラーキー/翻訳キーは既存仕様通り "equipmentCategoryNameJa"/"equipmentCategoryNameEn" 等になる
                const errorKey = `equipmentCategoryName${CapitalizedLang}`;
                const fieldTranslationKey = lang === 'ja'
                  ? 'features.MasterMaintenance.EquipmentCategory.field.name'
                  : `features.MasterMaintenance.EquipmentCategory.field.name${CapitalizedLang}`;
                const placeholderTranslationKey = lang === 'ja'
                  ? 'features.MasterMaintenance.EquipmentCategory.placeholder.name'
                  : `features.MasterMaintenance.EquipmentCategory.placeholder.name${CapitalizedLang}`;

                return (
                  <FormControl isInvalid={errors[errorKey]?.isError} h={70} key={`fc-equipmentCategoryName-${lang}`}>
                    <Flex align="start">
                      <FormLabel style={labelStyle}>
                        {t(fieldTranslationKey)}
                      </FormLabel>
                      <VStack align="left">
                        <Input
                          value={editTarget.equipmentCategoryName?.[lang] || ""}
                          onChange={(event) => { changeEditTarget("equipmentCategoryName", { ...editTarget.equipmentCategoryName, [lang]: event.target.value }) }}
                          placeholder={t(placeholderTranslationKey)}
                          width={valueWidth}
                          maxLength={InputLimits.EQUIPMENT_MASTER_NAME}
                          isInvalid={errors[errorKey]?.isError}
                        />
                        <FormErrorMessage mt={0}>{errors[errorKey]?.errorMessage}</FormErrorMessage>
                      </VStack>
                    </Flex>
                  </FormControl>
                );
              })}

              <FormControl h={100}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('features.MasterMaintenance.EquipmentCategory.field.description')}
                  </FormLabel>
                  <Textarea
                    value={editTarget.description || ""}
                    onChange={(event) => { changeEditTarget("description", event.target.value) }}
                    placeholder={t('features.MasterMaintenance.EquipmentCategory.placeholder.description')}
                    width={valueWidth}
                    maxLength={InputLimits.DESCRIPTION}
                    rows={3}
                  />
                </Flex>
              </FormControl>

              <FormControl isInvalid={errors.codePrefix?.isError} h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('features.MasterMaintenance.EquipmentCategory.field.codePrefix')}
                  </FormLabel>
                  <VStack align="left">
                    <HStack spacing={2}>
                      <Select
                        value={jisClassCode}
                        onChange={(event) => {
                          const newJis = event.target.value;
                          setJisClassCode(newJis);
                          changeEditTarget("codePrefix", newJis + detailCode);
                        }}
                        width="200px"
                        autoComplete="off"
                      >
                        {JIS_DEPARTMENT_CLASSES.map((cls) => (
                          <option key={cls.code} value={cls.code}>
                            {cls.code} - {cls.name}
                          </option>
                        ))}
                      </Select>
                      <Input
                        value={detailCode}
                        onChange={(event) => {
                          const val = event.target.value.replace(/[^0-9]/g, "").slice(0, 2);
                          setDetailCode(val);
                          changeEditTarget("codePrefix", jisClassCode + val);
                        }}
                        placeholder={t('features.MasterMaintenance.EquipmentCategory.placeholder.number')}
                        width="80px"
                        maxLength={2}
                        autoComplete="off"
                        isInvalid={errors.codePrefix?.isError}
                      />
                    </HStack>
                    <FormErrorMessage mt={0}>{errors.codePrefix?.errorMessage}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('common.label.sortOrder')}
                  </FormLabel>
                  <NumberInput
                    value={editTarget.sortOrder ?? 0}
                    onChange={(_, valueAsNumber) => { changeEditTarget("sortOrder", isNaN(valueAsNumber) ? 0 : valueAsNumber) }}
                    min={0}
                    max={99999}
                    step={100}
                    width={200}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </Flex>
              </FormControl>

              <FormControl isInvalid={errors.deleted?.isError} h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('common.label.deleteFlag')}
                  </FormLabel>
                  <VStack align="left">
                    <HStack>
                      <Button fontWeight="bold" userSelect="none" cursor="pointer" variant="ghost" _hover={{ bg: 'transparent' }}
                        onClick={() => {
                          changeEditTarget("deleted", false);
                        }}>{t('common.label.notDeleted')}</Button>

                      <Switch
                        isChecked={editTarget.deleted}
                        onChange={(event) => { changeEditTarget("deleted", event.target.checked) }}
                        isInvalid={errors.deleted?.isError}
                      />

                      <Button fontWeight="bold" userSelect="none" cursor="pointer" variant="ghost" _hover={{ bg: 'transparent' }}
                        onClick={() => {
                          changeEditTarget("deleted", true)
                        }}>{t('common.label.deleted')}</Button>
                    </HStack>
                    <FormErrorMessage mt={0}>{errors.deleted?.errorMessage}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>

              {editTarget.isCandidate && editTarget.registeredBy && (
                <FormControl>
                  <Flex align="start">
                    <FormLabel style={labelStyle}>
                      {t('common.label.registeredBy')}
                    </FormLabel>
                    <VStack align="left">
                      <Text>{editTarget.registeredBy}</Text>
                    </VStack>
                  </Flex>
                </FormControl>
              )}

              <FormControl isInvalid={errors.createDate?.isError}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('common.label.createDate')}
                  </FormLabel>
                  <VStack align="left">
                    <Text>{formatLocalDateTime(editTarget.createDate)}</Text>
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl isInvalid={errors.updateDate?.isError}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('common.label.updateDate')}
                  </FormLabel>
                  <VStack align="left">
                    <Text>{formatLocalDateTime(editTarget.updateDate)}</Text>
                  </VStack>
                </Flex>
              </FormControl>

            </VStack>
          </ModalBody>
          <ModalFooter>
            {editTarget.isCandidate && editTarget.updateDate && (
              <Button colorScheme="green" isDisabled={isSaving} isLoading={isSaving} onClick={(e) => {
                e.currentTarget.blur();
                handlePromote();
              }} m={1}>
                {t('common.button.confirmPromote')}
              </Button>
            )}
            <Button colorScheme="blue" isDisabled={isSaving} isLoading={isSaving} onClick={(e) => {
              e.currentTarget.blur();
              handleSave();
            }} m={1}>
              {t('common.button.save')}
            </Button>
            <Button onClick={handleClose} m={1}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default EquipmentCategory;
